<template>
    <form
        action="/explore"
        method="get"
        id="inline-search"
        class="inline-search"
    >
        <div class="flex-group">
            <div class="filters-main flex-1 flex-group">
                <div class="flex-col flex-4 br-xs">
                    <select-wrapper
                        labelText="Search by collection"
                        id="listing-collection"
                        :appendToBody="appendToBody"
                        placeholder="Search by collection"
                        :multiple="multiple"
                        :options="collections"
                        option-key="slug"
                        v-model="filters.collections"
                    >
                    </select-wrapper>
                </div>

                <div class="flex-col flex-6 flex-group filters">
                    <div
                        class="flex-col flex-6 has-flyout location-filter"
                        :class="{ visible: locations.length > 0 }"
                    >
                        <div
                            class="form-group lg in-field-label"
                            :class="{
                                populated: location || selectedLocation,
                                active: locationIsActive && !location,
                            }"
                            @click.prevent="$refs.locationInput.focus"
                        >
                            <label for="location-search-location"
                                >Location</label
                            >
                            <span class="placeholder"
                                >Enter state, suburb or city name</span
                            >
                            <input
                                id="location-search-location"
                                class="outline-white"
                                ref="locationInput"
                                :title="
                                    selectedLocation ? selectedLocation : ''
                                "
                                type="text"
                                v-model="location"
                                @focus="locationSearchFocused = true"
                                @blur="locationSearchFocused = false"
                            />
                            <button
                                type="button"
                                class="button tiny rounded grey clear-all"
                                :class="{ hidden: !selectedLocationType }"
                                @click="resetLocationSearch"
                            >
                                <svg-icon id="cross"></svg-icon>
                            </button>

                            <span
                                class="current-value"
                                :title="
                                    selectedLocation ? selectedLocation : ''
                                "
                            >
                                {{
                                    selectedLocation
                                        ? selectedLocation
                                        : "Location"
                                }}
                            </span>
                            <input
                                type="hidden"
                                :value="keywords"
                                name="keywords"
                            />
                            <input
                                type="hidden"
                                :value="locationId"
                                name="location"
                            />
                            <input
                                type="hidden"
                                :value="selectedLocation"
                                name="location-name"
                            />
                            <input
                                type="hidden"
                                value="25"
                                name="radius"
                                v-if="locationId"
                            />

                            <template v-for="collection in filters.collections">
                                <input
                                    type="hidden"
                                    :value="collection"
                                    name="collections"
                                />
                            </template>

                            <template v-for="availabilityType in filters.availabilityTypes">
                                <input
                                    type="hidden"
                                    :value="availabilityType"
                                    name="availabilityTypes"
                                />
                            </template>

                            <template v-for="spaceType in filters.spaceTypes">
                                <input
                                    type="hidden"
                                    :value="spaceType"
                                    name="spaceTypes"
                                />
                            </template>

                            <template v-for="propertyType in filters.propertyTypes">
                                <input
                                    type="hidden"
                                    :value="propertyType"
                                    name="propertyTypes"
                                />
                            </template>

                            <input
                                type="hidden"
                                :value="filters.rateDayFrom"
                                name="rateDayFrom"
                            />

                            <input
                                type="hidden"
                                :value="filters.rateDayTo"
                                name="rateDayTo"
                            />

                            <input
                                type="hidden"
                                :value="JSON.stringify(filters.metaCounts)"
                                name="metaCounts"
                            />

                            <input
                                type="hidden"
                                :value="filters.capacity"
                                name="capacity"
                            />
                        </div>

                        <div
                            class="flyout-menu region-results pos-left"
                            :class="{ hidden: locations.length === 0 }"
                        >
                            <vue-simplebar class="inner">
                                <ul
                                    id="location-regions"
                                    class="listing location-options"
                                >
                                    <li
                                        v-for="result in locations"
                                        @click="setLocation(result)"
                                    >
                                        <svg-icon
                                            :id="locationIcon(result)"
                                        ></svg-icon>
                                        {{ result.label }}
                                    </li>
                                </ul>
                            </vue-simplebar>
                        </div>
                    </div>
                </div>
                <div class="flex-row flex-2 flex-group buttons">
                        <div class="form-group lg submit">
                            <button type="submit" class="button primary uc home-search home-search-gtm" @click="handleSearchClick">
                                Search
                            </button>
                        </div>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
import debounce from "lodash/debounce";

export default {
    props: {
        propertyTypes: {
            type: Array,
        },
    },

    data() {
        const filters = {
            availabilityTypes: [],
            propertyTypes: [],
            spaceTypes: [],
            rateDayFrom: null,
            rateDayTo: null,
            capacity: null,
            collections: [],
            metaCounts: null
        };

        return {
            keywords: null,
            collections: [],
            location: "",
            locationId: null,
            locations: [],
            locationSearchFocused: false,
            selectedLocation: null,
            selectedLocationType: null,
            multiple: true,
            appendToBody: true,
            filters,
            watchFilters: false,
        };
    },

    mounted() {
        this.fetchCollections();
    },

    computed: {
        locationIsActive() {
            return !!this.selectedLocation;
        },
        currentExtraFilters() {
            return {
                availabilityTypes: this.filters.availabilityTypes,
                propertyTypes: this.filters.propertyTypes,
                spaceTypes: this.filters.spaceTypes,
                rateDayFrom: this.filters.rateDayFrom,
                rateDayTo: this.filters.rateDayTo,
                capacity: this.filters.capacity,
                metaCounts: this.filters.metaCounts
            };
        },
    },

    watch: {
        location: debounce(function () {
            if (
                (this.locationIsActive && this.location === "") ||
                this.location.length < 3
            ) {
                return;
            }

            this.fetchLocations();
        }, 250),
    },

    methods: {
        clearSelectedLocation() {
            this.selectedLocation = null;
        },

        fetchCollections() {
            axios
                .get("/api/form-meta/collections")
                .then((response) => {
                    this.collections = response.data || [];
                })
                .catch((error) => {});
        },

        fetchLocations() {
            if (!this.location || this.location === "") {
                return;
            }
            axios
                .get("/api/locations", { params: { query: this.location } })

                .then((response) => {
                    this.locations = response.data || [];
                })
                .catch((error) => {});
        },

        locationIcon(result) {
            switch (result.type) {
                case "lga":
                    return "tentMoon";
                case "region":
                    return "toggleMap";
            }

            return "mapPin";
        },

        setLocation(result) {
            this.locationId = result.type + ":" + result.id;
            this.selectedLocation = result.label;
            this.selectedLocationType = result.type;
            this.location = "";
            this.locations = [];
        },

        resetLocationSearch() {
            this.locationId = null;
            this.selectedLocation = "";
            this.selectedLocationType = null;
        },

        showAdvancedFilters() {
            this.$store
                .dispatch("modals/show", {
                    title: "More Filters",
                    type: "listing-advanced-filters",
                    data: this.currentExtraFilters,
                    wide: true
                })
                .then((resolved) => {
                    for (let prop in resolved.related) {
                        this.$set(this.filters, prop, resolved.related[prop]);
                    }
                    merge(this.filters, resolved.related);
                });
        },

        handleSearchClick() {
            this.$store.dispatch("intercom/track", {
              type: "Search Button",
              meta: {
                categories: this.filters.collections,
                location: this.selectedLocation
              }
            });
            console.log('Intercom New: Search Button', {categories: this.filters.collections, location: this.selectedLocation});
        }
    },
};
</script>
